import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import useFiltersState from 'state/useFiltersState';
import useUsersState from 'state/useUsersState';
import { getKamUserWithOrganizationFilter, getOrderFilters } from 'services/orders';
import { listOrganizations } from 'services/bussines';
import { DTOgetKamUserWithOrganization } from 'types/orders';
import useCountriesData from '../OrdersListingOperations/useCountriesData';
import { paymentMethodLabelsValue } from 'utils/variablesToTranslate';
import LabeledSwitchTextItem from 'components/LabeledSwitchTextItem/LabeledSwitchTextItem';
import ModuleFilters, { TgetFiltersList } from 'components/ModuleFilters/ModuleFilters';
import { IrawSelectOption } from 'components/ModuleFilters/ModuleFiltersExpandableSelect';
import useOrdersOperationsModule from '../OrdersListingOperations/useOrdersOperationsModule';
import useSyncAllFiltersFromFocusedValues from './useSyncAllFiltersFromFocusedValues';
import {
  useFilterClickHandlerString,
  useFilterClickHandlerNumber,
  useFilterClickHandlerBoolean,
  useFilterClickHandlerStringMulti,
  useFilterClickHandlerNumberMulti,
  TMultiSelectFocusedValue,
  TMultiSelectFocusedValueNumber
} from './useFilterClickHandler';
import { getMonthFilterOptions } from 'utils/formatDatesAndHours';
import {
  addIconsToSlaEtaOptions,
  extractValueFromOption,
  extractNameFromOption,
  stringToBoolean,
  isStringArray,
  isNumberArray
} from '../../trackingOrder.utils';
import {
  FILTER_NAMES,
  FILTER_TITLES,
  MISSING_NAME_TO_VALUE_MAP,
  MISSING_VALUE_TO_NAME_MAP,
  CONFIGURATION_FILTER_OPTIONS,
  CONFIGURATION_NAME_TO_VALUE_MAP,
  CONFIGURATION_VALUE_TO_NAME_MAP,
  INVOICE_FILTER_OPTIONS,
  INVOICE_NAME_TO_BOOLEAN_MAP,
  PREPARATION_OPTIONS_OBJECT,
  PREPARATION_OPTIONS_LIST,
  OTHERS_FILTER_OPTIONS,
  OTHERS_NAME_TO_VALUE_MAP,
  resolveOthersFilterSelection,
  ORDER_TYPE_FILTER_OPTIONS,
  ORDER_TYPE_NAME_TO_VALUE_MAP,
  SLA_ETA_FILTER_OPTIONS,
  SLA_ETA_NAME_TO_VALUE_MAP,
  SLA_ETA_VALUE_TO_NAME_MAP,
  SORT_OPTIONS_TO_VALUE_MAP,
  SORT_DEFAULT_SORT_BY,
  noneResponsible,
  ORDERS_FILTER_MAPPING,
  SORT_OPTIONS,
  ORDER_FILTERS_LIST_CONFIG,
  TFocusedFilterValue,
  TRawFilterValue,
  ISyncFilterEntry,
  TSyncFilterValue,
  MISSING_FILTER_OPTIONS
} from './filter.constants';
import { ISortFilter } from 'types/global';
import './OrdersTrackingOperationsFilters.scss';

const OrdersTrackingOperationsFilters = () => {
  const { ordersFilters, setOrdersFilters } = useFiltersState();
  const { users } = useUsersState();
  const { focusedFilterValues, setFocusedFilterValue, resetFocusedFilterValues, sortFilter, setSortFilter } =
    useOrdersOperationsModule();

  const setFocusedByFilterName = useMemo(
    () => ({
      [FILTER_NAMES.RESPONSIBLE]: (v: string | number | boolean | null) =>
        setFocusedFilterValue(FILTER_NAMES.RESPONSIBLE, v),
      [FILTER_NAMES.PREPARATION]: (v: string | number | boolean | null) =>
        setFocusedFilterValue(FILTER_NAMES.PREPARATION, v),
      [FILTER_NAMES.OTHERS]: (v: TFocusedFilterValue) => setFocusedFilterValue(FILTER_NAMES.OTHERS, v),
      [FILTER_NAMES.ORDER_TYPE]: (v: TFocusedFilterValue) => setFocusedFilterValue(FILTER_NAMES.ORDER_TYPE, v),
      [FILTER_NAMES.SLA_ETA]: (v: string | number | boolean | null) => setFocusedFilterValue(FILTER_NAMES.SLA_ETA, v),
      [FILTER_NAMES.KAM]: (v: string | number | boolean | null) => setFocusedFilterValue(FILTER_NAMES.KAM, v),
      [FILTER_NAMES.CONFIGURATION]: (v: string | number | boolean | null) =>
        setFocusedFilterValue(FILTER_NAMES.CONFIGURATION, v),
      [FILTER_NAMES.HAS_INVOICE]: (v: string | number | boolean | null) =>
        setFocusedFilterValue(FILTER_NAMES.HAS_INVOICE, v),
      [FILTER_NAMES.MISSING]: (v: string | number | boolean | null) => setFocusedFilterValue(FILTER_NAMES.MISSING, v),
      [FILTER_NAMES.COUNTRY_DESTINATION]: (v: string | number | boolean | null) =>
        setFocusedFilterValue(FILTER_NAMES.COUNTRY_DESTINATION, v),
      [FILTER_NAMES.MONTH]: (v: string | number | boolean | null) => setFocusedFilterValue(FILTER_NAMES.MONTH, v),
      [FILTER_NAMES.PAYMENT_TYPE]: (v: TFocusedFilterValue) => setFocusedFilterValue(FILTER_NAMES.PAYMENT_TYPE, v),
      [FILTER_NAMES.COMPANY]: (v: string | number | boolean | null) => setFocusedFilterValue(FILTER_NAMES.COMPANY, v)
    }),
    [setFocusedFilterValue]
  );

  const [openFilters, setOpenFilters] = useState<Record<string, boolean>>({});

  const setOpenFilter = useCallback(
    (filterName: string) => (valueOrUpdater: React.SetStateAction<boolean>) => {
      setOpenFilters(prev => {
        const next = { ...prev };
        const nextValue = typeof valueOrUpdater === 'function' ? valueOrUpdater(!!prev[filterName]) : valueOrUpdater;
        next[filterName] = nextValue;
        return next;
      });
    },
    []
  );

  const isFilterOpen = useCallback((filterName: string) => !!openFilters[filterName], [openFilters]);

  const { data: kamFiltersData, error: kamFiltersError } = useSWR('kam-user-filter', getKamUserWithOrganizationFilter, {
    revalidateOnFocus: false
  });

  const { data: orderFiltersData } = useSWR('order-filters-amount', getOrderFilters, {
    revalidateOnFocus: false
  });

  const { data: organizationsData, error: organizationsError } = useSWR('organizations-list', listOrganizations, {
    revalidateOnFocus: false
  });

  const { countryIdToNameMap, countriesOptions: countriesOptionsFromHook } = useCountriesData();

  const countriesOptionsFromAmountFilter = useMemo(() => {
    if (!orderFiltersData?.countries) return [];
    return orderFiltersData.countries;
  }, [orderFiltersData]);

  const countriesOptions = useMemo(() => {
    if (countriesOptionsFromAmountFilter?.length) return countriesOptionsFromAmountFilter;
    return countriesOptionsFromHook;
  }, [countriesOptionsFromAmountFilter, countriesOptionsFromHook]);

  const companyFilterRawOptions = useMemo(() => {
    if (!organizationsData) return [];
    return organizationsData.map(org => org.name).sort();
  }, [organizationsData]);

  const companyNameToValueMap = useMemo(() => {
    if (!organizationsData) return new Map<string, string>();
    return new Map(organizationsData.map(org => [org.name, String(org.id)]));
  }, [organizationsData]);

  const kamFilterRawOptions = useMemo(() => {
    if (!kamFiltersData) return [];
    return kamFiltersData.map((item: DTOgetKamUserWithOrganization) => item?.email || '');
  }, [kamFiltersData]);

  const kamNameToCodeMap = useMemo(() => {
    if (!kamFiltersData) return new Map<string, string>();
    return new Map(kamFiltersData.map((type: DTOgetKamUserWithOrganization) => [type.email, String(type.id)]));
  }, [kamFiltersData]);

  const paymentMethodRawOptionName = paymentMethodLabelsValue.map(item => item.name);
  const paymentMethodNameValue = useMemo(
    () => new Map(paymentMethodLabelsValue?.map(type => [type.name, type.value])),
    []
  );

  const responsibleUserRawOptions = [noneResponsible, ...users].map(user => user.email);
  const responsibleUserEmailToIdMap = useMemo(
    () => new Map([noneResponsible, ...users].map(user => [user.email, String(user.id)])),
    [users]
  );

  const responsibleUserEmailToNameMap = useMemo(
    () => new Map(users.map(user => [user.email, `${user.firstName} ${user.lastName}`])),
    [users]
  );

  const monthFilterOptions = useMemo(() => getMonthFilterOptions(), []);
  const monthFilterRawOptions = useMemo(() => monthFilterOptions.map(opt => opt.label), [monthFilterOptions]);

  const monthFilterLabelToValueMap = useMemo(
    () => new Map(monthFilterOptions.map(opt => [opt.label, opt.value])),
    [monthFilterOptions]
  );

  type TSingleValueGetFilterValue = (v: string | number | boolean) => string | null;

  interface IOrdersGetFilterValueFns {
    [FILTER_NAMES.RESPONSIBLE]: TSingleValueGetFilterValue;
    [FILTER_NAMES.PREPARATION]: TSingleValueGetFilterValue;
    [FILTER_NAMES.OTHERS]: TSingleValueGetFilterValue;
    [FILTER_NAMES.SLA_ETA]: TSingleValueGetFilterValue;
    [FILTER_NAMES.KAM]: TSingleValueGetFilterValue;
    [FILTER_NAMES.HAS_INVOICE]: TSingleValueGetFilterValue;
    [FILTER_NAMES.MISSING]: TSingleValueGetFilterValue;
    [FILTER_NAMES.PAYMENT_TYPE]: ISyncFilterEntry['getFilterValue'];
    [FILTER_NAMES.MONTH]: ISyncFilterEntry['getFilterValue'];
    [FILTER_NAMES.COMPANY]: ISyncFilterEntry['getFilterValue'];
  }

  const getFilterValueFns = useMemo((): IOrdersGetFilterValueFns => {
    const identityString: TSingleValueGetFilterValue = (v: string | number | boolean): string | null =>
      typeof v === 'string' ? v : null;
    const numberToString: TSingleValueGetFilterValue = (v: string | number | boolean): string | null =>
      typeof v === 'number' ? String(v) : null;
    const booleanToString: TSingleValueGetFilterValue = (v: string | number | boolean): string | null =>
      typeof v === 'boolean' ? String(v) : null;
    const paymentTypeGetter: ISyncFilterEntry['getFilterValue'] = (v: TRawFilterValue): TSyncFilterValue =>
      isStringArray(v) ? v : Array.isArray(v) ? null : typeof v === 'string' ? v : null;
    const monthGetter: ISyncFilterEntry['getFilterValue'] = (v: TRawFilterValue): TSyncFilterValue =>
      isStringArray(v) ? v : Array.isArray(v) ? null : typeof v === 'string' ? v : null;
    const companyGetter: ISyncFilterEntry['getFilterValue'] = (v: TRawFilterValue): TSyncFilterValue =>
      isStringArray(v) ? v : Array.isArray(v) ? null : typeof v === 'string' ? v : null;
    return {
      [FILTER_NAMES.RESPONSIBLE]: numberToString,
      [FILTER_NAMES.PREPARATION]: identityString,
      [FILTER_NAMES.OTHERS]: identityString,
      [FILTER_NAMES.SLA_ETA]: (v: string | number | boolean): string | null =>
        typeof v === 'string' ? SLA_ETA_VALUE_TO_NAME_MAP.get(v) || null : null,
      [FILTER_NAMES.KAM]: numberToString,
      [FILTER_NAMES.HAS_INVOICE]: booleanToString,
      [FILTER_NAMES.MISSING]: (v: string | number | boolean): string | null =>
        typeof v === 'string' ? MISSING_VALUE_TO_NAME_MAP.get(v) || null : null,
      [FILTER_NAMES.PAYMENT_TYPE]: paymentTypeGetter,
      [FILTER_NAMES.MONTH]: monthGetter,
      [FILTER_NAMES.COMPANY]: companyGetter
    };
  }, []);

  const getCountryFilterValueFn = useCallback(
    (v: TRawFilterValue): TSyncFilterValue => {
      if (isStringArray(v)) return v.length > 0 ? v : null;
      if (isNumberArray(v)) {
        if (v.length === 0) return null;
        return v.map(id => countryIdToNameMap.get(id)).filter((n): n is string => n != null);
      }
      return typeof v === 'number' ? countryIdToNameMap.get(v) || null : null;
    },
    [countryIdToNameMap]
  );

  const getConfigurationFilterValueFn = useCallback((v: TRawFilterValue): TSyncFilterValue => {
    if (isStringArray(v)) {
      return v.length > 0 ? v : null;
    }
    return typeof v === 'string' ? v : null;
  }, []);

  const getOthersFilterValueFn = useCallback((v: TRawFilterValue): TSyncFilterValue => {
    if (isStringArray(v)) {
      return v.length > 0 ? v : null;
    }
    return typeof v === 'string' ? v : null;
  }, []);

  const getOrderTypeFilterValueFn = useCallback((v: TRawFilterValue): TSyncFilterValue => {
    if (isStringArray(v)) {
      return v.length > 0 ? v : null;
    }
    return typeof v === 'string' ? v : null;
  }, []);

  const getCompanyFilterValueFn = useCallback(
    (v: TRawFilterValue): TSyncFilterValue => {
      if (isStringArray(v)) return v.length > 0 ? v : null;

      if (typeof v === 'string') {
        const id = companyNameToValueMap.get(v);
        return id != null ? [id] : null;
      }

      return null;
    },
    [companyNameToValueMap]
  );

  const getKamFilterValueFn = useCallback((v: TRawFilterValue): TSyncFilterValue => {
    if (isNumberArray(v)) {
      return v?.length > 0 ? v?.map(id => String(id)) : null;
    }
    return typeof v === 'number' ? String(v) : null;
  }, []);

  const toSyncGetFilterValue = useCallback(
    (fn: TSingleValueGetFilterValue): ISyncFilterEntry['getFilterValue'] =>
      (v): TSyncFilterValue =>
        Array.isArray(v) ? null : fn(v),
    []
  );

  const syncEntries = useMemo(
    (): ISyncFilterEntry[] => [
      {
        filterName: FILTER_NAMES.RESPONSIBLE,
        focusedValue: focusedFilterValues[FILTER_NAMES.RESPONSIBLE] ?? null,
        getFilterValue: toSyncGetFilterValue(getFilterValueFns[FILTER_NAMES.RESPONSIBLE])
      },
      {
        filterName: FILTER_NAMES.PREPARATION,
        focusedValue: focusedFilterValues[FILTER_NAMES.PREPARATION] ?? null,
        getFilterValue: toSyncGetFilterValue(getFilterValueFns[FILTER_NAMES.PREPARATION])
      },
      {
        filterName: FILTER_NAMES.OTHERS,
        focusedValue: focusedFilterValues[FILTER_NAMES.OTHERS] ?? null,
        getFilterValue: getOthersFilterValueFn
      },
      {
        filterName: FILTER_NAMES.ORDER_TYPE,
        focusedValue: focusedFilterValues[FILTER_NAMES.ORDER_TYPE] ?? null,
        getFilterValue: getOrderTypeFilterValueFn
      },
      {
        filterName: FILTER_NAMES.SLA_ETA,
        focusedValue: focusedFilterValues[FILTER_NAMES.SLA_ETA] ?? null,
        getFilterValue: toSyncGetFilterValue(getFilterValueFns[FILTER_NAMES.SLA_ETA])
      },
      {
        filterName: FILTER_NAMES.KAM,
        focusedValue: focusedFilterValues[FILTER_NAMES.KAM] ?? null,
        getFilterValue: getKamFilterValueFn
      },
      {
        filterName: FILTER_NAMES.CONFIGURATION,
        focusedValue: focusedFilterValues[FILTER_NAMES.CONFIGURATION] ?? null,
        getFilterValue: getConfigurationFilterValueFn
      },
      {
        filterName: FILTER_NAMES.HAS_INVOICE,
        focusedValue: focusedFilterValues[FILTER_NAMES.HAS_INVOICE] ?? null,
        getFilterValue: toSyncGetFilterValue(getFilterValueFns[FILTER_NAMES.HAS_INVOICE])
      },
      {
        filterName: FILTER_NAMES.MISSING,
        focusedValue: focusedFilterValues[FILTER_NAMES.MISSING] ?? null,
        getFilterValue: toSyncGetFilterValue(getFilterValueFns[FILTER_NAMES.MISSING])
      },
      {
        filterName: FILTER_NAMES.COUNTRY_DESTINATION,
        focusedValue: focusedFilterValues[FILTER_NAMES.COUNTRY_DESTINATION] ?? null,
        getFilterValue: getCountryFilterValueFn
      },
      {
        filterName: FILTER_NAMES.MONTH,
        focusedValue: focusedFilterValues[FILTER_NAMES.MONTH] ?? null,
        getFilterValue: getFilterValueFns[FILTER_NAMES.MONTH]
      },
      {
        filterName: FILTER_NAMES.PAYMENT_TYPE,
        focusedValue: focusedFilterValues[FILTER_NAMES.PAYMENT_TYPE] ?? null,
        getFilterValue: getFilterValueFns[FILTER_NAMES.PAYMENT_TYPE]
      },
      {
        filterName: FILTER_NAMES.COMPANY,
        focusedValue: focusedFilterValues[FILTER_NAMES.COMPANY] ?? null,
        getFilterValue: getCompanyFilterValueFn
      }
    ],
    [
      focusedFilterValues,
      getFilterValueFns,
      getCountryFilterValueFn,
      getCompanyFilterValueFn,
      getKamFilterValueFn,
      getConfigurationFilterValueFn,
      getOthersFilterValueFn,
      getOrderTypeFilterValueFn,
      toSyncGetFilterValue
    ]
  );

  useSyncAllFiltersFromFocusedValues({ syncEntries, setOrdersFilters });

  const getCurrentOthersFocusedValue = useCallback((): string[] | string | null => {
    const v = useOrdersOperationsModule.getState().focusedFilterValues[FILTER_NAMES.OTHERS];
    if (v == null) return null;
    if (isStringArray(v)) return v;
    return typeof v === 'string' ? v : null;
  }, []);

  const setOthersFocusedValue = useCallback(
    (value: TMultiSelectFocusedValue) => setFocusedFilterValue(FILTER_NAMES.OTHERS, value),
    [setFocusedFilterValue]
  );

  /**
   * Maneja la selección del filtro Otros con exclusión mutua entre parada y sin parada en bodega.
   */
  const handleOthersFilterClick = useCallback(
    (param: IrawSelectOption | string) => {
      const filterValue = typeof param === 'string' ? param : extractValueFromOption(param);

      if (!filterValue) return;

      const current = getCurrentOthersFocusedValue();
      const currentValues: string[] = Array.isArray(current)
        ? current
        : current != null && current !== ''
        ? [String(current)]
        : [];
      const isAlreadySelected = currentValues.includes(filterValue);
      const nextValues = resolveOthersFilterSelection(filterValue, currentValues, !isAlreadySelected);
      const nextFocused: TMultiSelectFocusedValue = nextValues.length > 0 ? nextValues : null;

      setOthersFocusedValue(nextFocused);

      const currentFilters = [...ordersFilters];
      const filterIndex = currentFilters.findIndex(filter => filter.name === FILTER_NAMES.OTHERS);

      if (nextFocused === null) {
        if (filterIndex >= 0) {
          currentFilters.splice(filterIndex, 1);
          setOrdersFilters(currentFilters);
        }
        return;
      }

      if (filterIndex >= 0) {
        currentFilters[filterIndex] = {
          ...currentFilters[filterIndex],
          values: nextFocused
        };
      } else {
        currentFilters.push({
          name: FILTER_NAMES.OTHERS,
          includes: 'in',
          values: nextFocused
        });
      }

      setOrdersFilters(currentFilters);
    },
    [getCurrentOthersFocusedValue, ordersFilters, setOrdersFilters, setOthersFocusedValue]
  );

  const getCurrentOrderTypeFocusedValue = useCallback((): string[] | string | null => {
    const v = useOrdersOperationsModule.getState().focusedFilterValues[FILTER_NAMES.ORDER_TYPE];
    if (v == null) return null;
    if (isStringArray(v)) return v;
    return typeof v === 'string' ? v : null;
  }, []);

  const setOrderTypeFocusedValue = useCallback(
    (value: TMultiSelectFocusedValue) => setFocusedFilterValue(FILTER_NAMES.ORDER_TYPE, value),
    [setFocusedFilterValue]
  );

  const handleOrderTypeFilterClick = useFilterClickHandlerStringMulti({
    filterName: FILTER_NAMES.ORDER_TYPE,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setOrderTypeFocusedValue,
    getCurrentFocusedValue: getCurrentOrderTypeFocusedValue,
    extractValue: extractValueFromOption
  });

  const handleSlaEtaFilterClick = useFilterClickHandlerString({
    filterName: FILTER_NAMES.SLA_ETA,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setFocusedByFilterName[FILTER_NAMES.SLA_ETA],
    extractValue: extractValueFromOption,
    transformToFocusedValue: (slaEtaName: string) => SLA_ETA_NAME_TO_VALUE_MAP.get(slaEtaName) || null
  });

  const handleResponsibleFilterClick = useFilterClickHandlerNumber({
    filterName: FILTER_NAMES.RESPONSIBLE,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setFocusedByFilterName[FILTER_NAMES.RESPONSIBLE],
    transformToFocusedValue: (responsibleIdString: string) => parseInt(responsibleIdString, 10)
  });

  const handlePreparationFilterClick = useFilterClickHandlerString({
    filterName: FILTER_NAMES.PREPARATION,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setFocusedByFilterName[FILTER_NAMES.PREPARATION]
  });

  const getCurrentKamFocusedValue = useCallback((): number[] | number | null => {
    const v = useOrdersOperationsModule?.getState()?.focusedFilterValues[FILTER_NAMES.KAM];
    if (v == null || !v) return null;
    if (isNumberArray(v)) return v;
    return typeof v === 'number' ? v : null;
  }, []);

  const setKamFocusedValue = useCallback(
    (value: TMultiSelectFocusedValueNumber) => setFocusedFilterValue(FILTER_NAMES.KAM, value),
    [setFocusedFilterValue]
  );

  const handleKamFilterClick = useFilterClickHandlerNumberMulti({
    filterName: FILTER_NAMES.KAM,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setKamFocusedValue,
    getCurrentFocusedValue: getCurrentKamFocusedValue,
    transformToFocusedValue: (kamIdString: string) => parseInt(kamIdString, 10),
    getFilterValuesFromFocused: (focused: number[]) => focused.map(id => String(id))
  });

  const handleMissingFilterClick = useFilterClickHandlerString({
    filterName: FILTER_NAMES.MISSING,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setFocusedByFilterName[FILTER_NAMES.MISSING],
    extractValue: extractValueFromOption,
    transformToFocusedValue: (missingName: string) => MISSING_NAME_TO_VALUE_MAP.get(missingName) || null
  });

  const getCurrentConfigFocusedValue = useCallback((): string[] | string | null => {
    const v = useOrdersOperationsModule.getState().focusedFilterValues[FILTER_NAMES.CONFIGURATION];
    if (v == null) return null;
    if (isStringArray(v)) return v;
    return typeof v === 'string' ? v : null;
  }, []);

  const setConfigFocusedValue = useCallback(
    (value: TMultiSelectFocusedValue) => setFocusedFilterValue(FILTER_NAMES.CONFIGURATION, value),
    [setFocusedFilterValue]
  );

  const handleConfigurationFilterClick = useFilterClickHandlerStringMulti({
    filterName: FILTER_NAMES.CONFIGURATION,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setConfigFocusedValue,
    getCurrentFocusedValue: getCurrentConfigFocusedValue,
    extractValue: extractValueFromOption
  });

  const handleInvoiceFilterClick = useFilterClickHandlerBoolean({
    filterName: FILTER_NAMES.HAS_INVOICE,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setFocusedByFilterName[FILTER_NAMES.HAS_INVOICE],
    transformToFocusedValue: stringToBoolean
  });

  const getCurrentPaymentFocusedValue = useCallback((): string[] | string | null => {
    const v = useOrdersOperationsModule.getState().focusedFilterValues[FILTER_NAMES.PAYMENT_TYPE];
    if (v == null) return null;
    if (isStringArray(v)) return v;
    return typeof v === 'string' ? v : null;
  }, []);

  const setPaymentTypeFocusedValue = useCallback(
    (value: TMultiSelectFocusedValue) => setFocusedFilterValue(FILTER_NAMES.PAYMENT_TYPE, value),
    [setFocusedFilterValue]
  );

  const handlePaymentMethodFilterClick = useFilterClickHandlerStringMulti({
    filterName: FILTER_NAMES.PAYMENT_TYPE,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setPaymentTypeFocusedValue,
    getCurrentFocusedValue: getCurrentPaymentFocusedValue
  });

  const getCurrentMonthFocusedValue = useCallback((): string[] | string | null => {
    const v = useOrdersOperationsModule.getState().focusedFilterValues[FILTER_NAMES.MONTH];
    if (v == null) return null;
    if (isStringArray(v)) return v;
    return typeof v === 'string' ? v : null;
  }, []);

  const setMonthFocusedValue = useCallback(
    (value: TMultiSelectFocusedValue) => setFocusedFilterValue(FILTER_NAMES.MONTH, value),
    [setFocusedFilterValue]
  );

  const handleMonthFilterClick = useFilterClickHandlerStringMulti({
    filterName: FILTER_NAMES.MONTH,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setMonthFocusedValue,
    getCurrentFocusedValue: getCurrentMonthFocusedValue
  });

  const getCurrentCountryFocusedValue = useCallback((): string[] | string | null => {
    const v = useOrdersOperationsModule.getState().focusedFilterValues[FILTER_NAMES.COUNTRY_DESTINATION];
    if (v == null) return null;
    if (isStringArray(v)) return v;
    return typeof v === 'string' ? v : null;
  }, []);

  const setCountryFocusedValue = useCallback(
    (value: TMultiSelectFocusedValue) => setFocusedFilterValue(FILTER_NAMES.COUNTRY_DESTINATION, value),
    [setFocusedFilterValue]
  );

  const handleCountryFilterClick = useFilterClickHandlerStringMulti({
    filterName: FILTER_NAMES.COUNTRY_DESTINATION,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setCountryFocusedValue,
    getCurrentFocusedValue: getCurrentCountryFocusedValue,
    extractValue: extractNameFromOption
  });

  const getCurrentCompanyFocusedValue = useCallback((): string[] | string | null => {
    const v = useOrdersOperationsModule.getState().focusedFilterValues[FILTER_NAMES.COMPANY];
    if (v == null) return null;
    if (isStringArray(v)) return v;
    return typeof v === 'string' ? v : null;
  }, []);

  const setCompanyFocusedValue = useCallback(
    (value: TMultiSelectFocusedValue) => setFocusedFilterValue(FILTER_NAMES.COMPANY, value),
    [setFocusedFilterValue]
  );

  const handleCompanyFilterClick = useFilterClickHandlerStringMulti({
    filterName: FILTER_NAMES.COMPANY,
    ordersFilters,
    setOrdersFilters,
    setFocusedValue: setCompanyFocusedValue,
    getCurrentFocusedValue: getCurrentCompanyFocusedValue,
    extractValue: (option: IrawSelectOption) => (option?.value != null ? String(option.value) : '')
  });

  const resetSortToDefaults = useCallback(() => {
    const currentFilters = ordersFilters.filter(filter => filter.name !== FILTER_NAMES.SORT);
    setOrdersFilters([
      ...currentFilters,
      {
        name: FILTER_NAMES.SORT,
        includes: 'in',
        values: [SORT_DEFAULT_SORT_BY]
      }
    ]);
    setSortFilter({ value: 'desc' });
  }, [ordersFilters, setOrdersFilters, setSortFilter]);

  const handleSortByClick = useCallback(
    (option: IrawSelectOption) => {
      if (!option?.value) return;
      const currentFilters = [...ordersFilters];
      const sortFilterIndex = currentFilters.findIndex(filter => filter.name === FILTER_NAMES.SORT);

      if (sortFilterIndex >= 0) {
        currentFilters[sortFilterIndex] = { ...currentFilters[sortFilterIndex], values: [option.value] };
      } else {
        currentFilters.push({ name: FILTER_NAMES.SORT, includes: 'in', values: [option.value] });
      }
      setOrdersFilters(currentFilters);
    },
    [ordersFilters, setOrdersFilters]
  );

  const filterClickHandlers: Record<string, (option: IrawSelectOption) => void> = useMemo(
    () => ({
      [FILTER_NAMES.COMPANY]: handleCompanyFilterClick,
      [FILTER_NAMES.MONTH]: handleMonthFilterClick,
      [FILTER_NAMES.COUNTRY_DESTINATION]: handleCountryFilterClick,
      [FILTER_NAMES.PAYMENT_TYPE]: handlePaymentMethodFilterClick,
      [FILTER_NAMES.HAS_INVOICE]: option => handleInvoiceFilterClick(option?.value || ''),
      [FILTER_NAMES.KAM]: handleKamFilterClick,
      [FILTER_NAMES.RESPONSIBLE]: option => handleResponsibleFilterClick(option?.value || ''),
      [FILTER_NAMES.MISSING]: handleMissingFilterClick,
      [FILTER_NAMES.SLA_ETA]: handleSlaEtaFilterClick,
      [FILTER_NAMES.CONFIGURATION]: handleConfigurationFilterClick,
      [FILTER_NAMES.PREPARATION]: option => handlePreparationFilterClick(option?.value || ''),
      [FILTER_NAMES.OTHERS]: handleOthersFilterClick,
      [FILTER_NAMES.ORDER_TYPE]: handleOrderTypeFilterClick,
      [FILTER_NAMES.SORT]: handleSortByClick
    }),
    [
      handleCompanyFilterClick,
      handleMonthFilterClick,
      handleCountryFilterClick,
      handlePaymentMethodFilterClick,
      handleInvoiceFilterClick,
      handleKamFilterClick,
      handleResponsibleFilterClick,
      handleMissingFilterClick,
      handleSlaEtaFilterClick,
      handleConfigurationFilterClick,
      handlePreparationFilterClick,
      handleOthersFilterClick,
      handleOrderTypeFilterClick,
      handleSortByClick
    ]
  );

  const getRawOptionsForFilter = useCallback(
    (
      filterName: string,
      filterTitle: string,
      formModuleFilters: (
        filterTitle: string,
        defaultRawFilterOptions?: string[],
        nameToValueMap?: Map<string, string>
      ) => IrawSelectOption[]
    ): IrawSelectOption[] => {
      if (filterName === FILTER_NAMES.COMPANY) {
        return formModuleFilters(filterTitle, companyFilterRawOptions, companyNameToValueMap);
      }
      if (filterName === FILTER_NAMES.MONTH) {
        return formModuleFilters(filterTitle, monthFilterRawOptions, monthFilterLabelToValueMap);
      }
      if (filterName === FILTER_NAMES.COUNTRY_DESTINATION) {
        return formModuleFilters(filterTitle, countriesOptions);
      }
      if (filterName === FILTER_NAMES.PAYMENT_TYPE) {
        return formModuleFilters(filterTitle, paymentMethodRawOptionName, paymentMethodNameValue);
      }
      if (filterName === FILTER_NAMES.HAS_INVOICE) {
        return formModuleFilters(filterTitle, INVOICE_FILTER_OPTIONS, INVOICE_NAME_TO_BOOLEAN_MAP);
      }
      if (filterName === FILTER_NAMES.KAM) {
        return formModuleFilters(filterTitle, kamFilterRawOptions, kamNameToCodeMap);
      }
      if (filterName === FILTER_NAMES.RESPONSIBLE) {
        return formModuleFilters(filterTitle, responsibleUserRawOptions, responsibleUserEmailToIdMap).map(option => ({
          ...option,
          lineTitle: responsibleUserEmailToNameMap.get(option.name)
        }));
      }
      if (filterName === FILTER_NAMES.MISSING) {
        return formModuleFilters(filterTitle, MISSING_FILTER_OPTIONS);
      }
      if (filterName === FILTER_NAMES.SLA_ETA) {
        return addIconsToSlaEtaOptions(formModuleFilters(filterTitle, SLA_ETA_FILTER_OPTIONS));
      }
      if (filterName === FILTER_NAMES.CONFIGURATION) {
        return formModuleFilters(filterTitle, CONFIGURATION_FILTER_OPTIONS, CONFIGURATION_NAME_TO_VALUE_MAP);
      }
      if (filterName === FILTER_NAMES.PREPARATION) {
        return formModuleFilters(filterTitle, Object.keys(PREPARATION_OPTIONS_OBJECT), PREPARATION_OPTIONS_LIST);
      }
      if (filterName === FILTER_NAMES.OTHERS) {
        return formModuleFilters(filterTitle, OTHERS_FILTER_OPTIONS, OTHERS_NAME_TO_VALUE_MAP);
      }
      if (filterName === FILTER_NAMES.ORDER_TYPE) {
        return formModuleFilters(filterTitle, ORDER_TYPE_FILTER_OPTIONS, ORDER_TYPE_NAME_TO_VALUE_MAP);
      }
      if (filterName === FILTER_NAMES.SORT) {
        return formModuleFilters(filterTitle, SORT_OPTIONS, SORT_OPTIONS_TO_VALUE_MAP);
      }
      return [];
    },
    [
      companyFilterRawOptions,
      companyNameToValueMap,
      monthFilterRawOptions,
      monthFilterLabelToValueMap,
      countriesOptions,
      paymentMethodRawOptionName,
      paymentMethodNameValue,
      kamFilterRawOptions,
      kamNameToCodeMap,
      responsibleUserRawOptions,
      responsibleUserEmailToIdMap,
      responsibleUserEmailToNameMap
    ]
  );

  const getOrdersFiltersList: TgetFiltersList = (formModuleFilters, handleClickFilterOption, getFilterActiveStatus) =>
    ORDER_FILTERS_LIST_CONFIG.map(config => ({
      title: config.filterTitle,
      openFunction: setOpenFilter(config.filterName),
      openStatus: isFilterOpen(config.filterName),
      rawSelectOptions: getRawOptionsForFilter(config.filterName, config.filterTitle, formModuleFilters),
      handleChangeSelectedOption: filterClickHandlers[config.filterName],
      isFilterActive: getFilterActiveStatus(config.filterName),
      showBubbleInformation: config.filterName !== FILTER_NAMES.SORT,
      ...(config.showSearch !== undefined && { showSearch: config.showSearch }),
      ...(config.searchPlaceholder && { searchPlaceholder: config.searchPlaceholder }),
      ...(config.sortAlphabetically && { sortAlphabetically: config.sortAlphabetically }),
      ...(config.titleFilterContainer && { titleFilterContainer: config.titleFilterContainer }),
      ...(config.useRadioButtons && { useRadioButtons: config.useRadioButtons })
    }));

  const initializeSortFilter = useCallback(() => {
    const sortFilterExists = ordersFilters.some(filter => filter.name === FILTER_NAMES.SORT);
    if (!sortFilterExists) resetSortToDefaults();
  }, [ordersFilters, resetSortToDefaults]);

  const handleKamFiltersError = () => {
    if (kamFiltersError) {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener los filtros de KAM</>
      });
    }
  };

  const handleOrganizationsError = () => {
    if (organizationsError) {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener las organizaciones</>
      });
    }
  };

  const handleSortChange = useCallback(
    (sortFilter: ISortFilter) => {
      setSortFilter(sortFilter);
    },
    [setSortFilter]
  );

  const handleResetFilters = useCallback(() => {
    resetFocusedFilterValues();
    resetSortToDefaults();
  }, [resetFocusedFilterValues, resetSortToDefaults]);

  const handleWarehouseFilterChange = useCallback(
    (checked: boolean) => {
      setFocusedFilterValue(FILTER_NAMES.WAREHOUSE_FILTER, !!checked);
    },
    [setFocusedFilterValue]
  );

  useEffect(() => {
    handleKamFiltersError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kamFiltersError]);

  useEffect(() => {
    handleOrganizationsError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationsError]);

  useEffect(() => {
    initializeSortFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="ordersTrackingOperationsFilters">
      <ModuleFilters
        moduleFilters={ordersFilters}
        setModuleFilters={setOrdersFilters}
        getFiltersList={getOrdersFiltersList}
        moduleFiltersTitlesMapping={ORDERS_FILTER_MAPPING}
        customGap={8}
        moduleFiltersIconContainerCustomClass="moduleFiltersIconContainerCustomClassOrdersTrackingOperations"
        onResetFilters={handleResetFilters}
        showSort
        showSortInFilter={FILTER_TITLES.SORT}
        onSortChange={handleSortChange}
        singleSelectFilters={{ sort: true, paymentType: true }}
        customHeightContainer="customHeightContainerOrdersTrackingOperations"
        showReferenceLetters
        customSortClassName="customSortClassNameOrdersTrackingOperations"
        currentSortFilter={sortFilter || undefined}
      />
      <div className="warehouseFilterRow">
        <LabeledSwitchTextItem
          label={FILTER_TITLES.WAREHOUSE_FILTER}
          value={!!focusedFilterValues[FILTER_NAMES.WAREHOUSE_FILTER]}
          onChange={handleWarehouseFilterChange}
          tooltipProps={{
            message: (
              <>
                Filtrar rapidamente ordenes <br /> solo relacionadas con bodega
              </>
            ),
            position: 'bottomRight'
          }}
        />
      </div>
    </div>
  );
};

export default OrdersTrackingOperationsFilters;
